import React from 'react'
import { useTranslation } from 'react-i18next'
import { Line } from 'react-chartjs-2'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatMoney } from '../lib/money'
import { hasTrend, trendSeries, type TrendKey } from '../lib/overview'
import type { CostTrendPoint } from '../types'

const COLOR: Record<TrendKey, string> = { revisedBudget: '#64748b', committed: '#3b82f6', actual: '#22c55e', projectedFinalCost: '#f97316' }
const LABEL_KEY: Record<TrendKey, string> = {
  revisedBudget: 'CostControl_col_revisedBudget', committed: 'CostControl_col_committed', actual: 'CostControl_col_actual', projectedFinalCost: 'CostControl_col_projectedFinalCost',
}

interface Props { points: CostTrendPoint[]; currency: string; locale: string }

/** 1b-C spec §4: the nightly snapshots plus today's live point (chart.js 2 options). */
const TrendChart: React.FC<Props> = ({ points, currency, locale }) => {
  const { t } = useTranslation()
  const money = (v: number) => formatMoney(String(v), { locale, currency, whole: true })
  const { labels, series } = trendSeries(points)
  const data = {
    labels: labels.map((d) => new Date(`${d}T00:00:00Z`).toLocaleDateString(locale, { day: 'numeric', month: 'numeric', timeZone: 'UTC' })),
    datasets: series.map((s) => ({
      label: t(LABEL_KEY[s.key]), data: s.data, borderColor: COLOR[s.key], backgroundColor: COLOR[s.key], fill: false, lineTension: 0, pointRadius: 2, borderWidth: 2,
    })),
  }
  const options = {
    maintainAspectRatio: false,
    legend: { position: 'bottom' as const },
    tooltips: {
      mode: 'index' as const,
      intersect: false,
      callbacks: { label: (item: any, d: any) => `${d.datasets[item.datasetIndex].label}: ${money(Number(item.yLabel))}` },
    },
    scales: { yAxes: [{ ticks: { callback: (v: number) => money(v) } }] },
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{t('CostControl_overview_trend')}</CardTitle>
      </CardHeader>
      <CardContent>
        {hasTrend(points)
          ? <div className="h-[280px]"><Line data={data} options={options} /></div>
          : <p className="text-sm text-muted-foreground">{t('CostControl_overview_trend_empty')}</p>}
      </CardContent>
    </Card>
  )
}
export default TrendChart
